import { useSearchParamsContext } from '@internal/blocks/SearchResultsBlock/SearchParamsContext';
import { SearchResultsSortKey } from '@internal/blocks/SearchResultsBlock/SearchResultsSortKey';
import { SearchModel } from '@internal/blocks/SearchResultsBlock/TSearchParams';
import { onChangeDebounceTime } from '@internal/utils/constants';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { Clear as ClearIcon } from '@mui/icons-material';
import { IconButton, TextField } from '@mui/material';
import { ModelSelectFilter } from 'blocks/SearchCoverBlock/ModelSelectFilter';
import { useSearchResultsRouting } from 'blocks/SearchResultsBlock/useSearchResultsRouting';
import { translations } from 'i18n/translations';
import { DebouncedFunc, debounce } from 'lodash';
import { useTranslation } from 'next-i18next';
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { tracker } from 'utils/tracking/tracker';
import { SearchFilterCategory } from './SearchFilterCategory/SearchFilterCategory';

export const ModelFilter: React.FC = React.memo(() => {
    const { searchParams } = useSearchParamsContext();
    const { gotoSearchResults } = useSearchResultsRouting();
    const { t } = useTranslation(localeNamespace.common);
    const [inputValue, setInputValue] = useState('');
    const isFocused = useRef(false);

    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    const trackModelsChange = useCallback((models: SearchModel[]) => {
        if (models.length > 0) {
            tracker.trackEvent({
                name: 'filter-applied',
                filter_placement: 'serp',
                filter_category: 'model',
                filter_model: models.map(m => m.name).join(','),
                is_interaction: true,
            });
        }
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: unknown effect of fix
    const handleModelSelectOnChange = useCallback(
        (models: SearchModel[]) => {
            trackModelsChange(models);

            gotoSearchResults({
                ...searchParamsRef.current,
                models,
                sort: models.some(m => m.type === 'fuzzy')
                    ? undefined
                    : searchParamsRef.current.sort,
                amount: undefined,
            });
        },
        [searchParamsRef, gotoSearchResults, trackModelsChange],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: unknown effect of fix
    const submitChanges: DebouncedFunc<(s: string) => void> = useCallback(
        debounce((newValue: string) => {
            const fuzzyModelValues = newValue
                .split(',')
                .map(v => v.trim())
                .filter(v => v.length > 0);

            const newModels = searchParamsRef.current.models.filter(
                m => m.type !== 'fuzzy',
            );

            for (const value of fuzzyModelValues) {
                newModels.push({
                    name: value,
                    type: 'fuzzy',
                });
            }
            let sort: SearchResultsSortKey | undefined =
                searchParamsRef.current.sort;

            if (newModels.length > 0) {
                trackModelsChange(newModels);
            }

            if (fuzzyModelValues.length > 0) {
                // remove user defined sorting and fall back to default
                sort = undefined;
            }

            gotoSearchResults({
                ...searchParamsRef.current,
                models: newModels,
                sort,
                amount: undefined,
            });
        }, onChangeDebounceTime),
        [gotoSearchResults, trackModelsChange],
    );

    const handleInputBlur = useCallback(() => {
        submitChanges.flush();
        isFocused.current = false;
    }, [submitChanges]);

    const handleInputFocus = useCallback(() => {
        isFocused.current = true;
    }, []);

    const handleInputChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            const newValue = ev.target.value;
            setInputValue(newValue);
            submitChanges(newValue);
        },
        [submitChanges],
    );

    const clearInput = useCallback(() => {
        setInputValue('');
        submitChanges('');
        submitChanges.flush();
    }, [submitChanges]);

    useEffect(() => {
        // flush on unmount
        return () => submitChanges.flush();
    }, [submitChanges]);

    useEffect(() => {
        // we can only update the input value if the input is not focused / if the user is not actively making changes
        if (!isFocused.current) {
            const fuzzyModelInput = searchParams.models
                .filter(m => m.type === 'fuzzy')
                .map(m => m.name)
                .join(', ');
            setInputValue(fuzzyModelInput);
        }
    }, [searchParams.models]);

    return (
        <>
            <SearchFilterCategory
                title={t(translations.searchResultsFiltersModelKeywordLabel)}
            >
                <TextField
                    size="small"
                    autoComplete="off"
                    fullWidth
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={handleInputFocus}
                    value={inputValue}
                    InputProps={{
                        endAdornment: (
                            <IconButton
                                size="small"
                                disabled={!inputValue}
                                onClick={clearInput}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        ),
                    }}
                />
            </SearchFilterCategory>

            <SearchFilterCategory
                title={t(translations.searchResultsFiltersModelLabel)}
            >
                <ModelSelectFilter
                    brands={searchParams.brands}
                    models={searchParams.models}
                    onChange={handleModelSelectOnChange}
                    closeOnSelect
                    machineType={searchParams.machineType ?? null}
                />
            </SearchFilterCategory>
        </>
    );
});

ModelFilter.displayName = 'ModelFilter';
